import { IconCheck, IconX } from '@tabler/icons-react';
import {
  AutoPayStatus,
  EmployeePaymentMethodType,
  InitialPremiumPaymentStatus,
} from '@zorro/clients';
import { Pill, PillColor } from '@zorro/design-system/v2/Pill';
import { Tooltip } from '@zorro/design-system/v2/Tooltip';
import {
  IconHalfCircle,
  IconZorroPaymentCard,
  IconZorroPaymentCombined,
  IconZorroPaymentSelfPay,
} from '@zorro/shared/assets';
import { formatToFixedFloat } from '@zorro/shared/formatters';
import Decimal from 'decimal.js';
import { ComponentType, ReactNode } from 'react';

const paymentMethodToProps: Record<
  EmployeePaymentMethodType,
  { label: string; color: PillColor; Icon: ComponentType }
> = {
  [EmployeePaymentMethodType.ZORRO_PAY]: {
    label: 'Zorro Pay',
    color: 'indigo',
    Icon: IconZorroPaymentCard,
  },
  [EmployeePaymentMethodType.SELF_PAY]: {
    label: 'Self Pay',
    color: 'yellow',
    Icon: IconZorroPaymentSelfPay,
  },
  [EmployeePaymentMethodType.COMBINED]: {
    label: 'Combined',
    color: 'blue',
    Icon: IconZorroPaymentCombined,
  },
};

interface PaymentMethodBadgeProps {
  paymentMethod: EmployeePaymentMethodType;
  premium: number;
  selfPayAmount: number;
}

const tooltipCombinedText = (zorroPay: number, selfPay: number) =>
  `$${formatToFixedFloat(zorroPay)} through Zorro Pay and $${formatToFixedFloat(
    selfPay
  )} through Self-pay`;

export const PaymentMethodBadge = ({
  paymentMethod,
  premium,
  selfPayAmount,
}: PaymentMethodBadgeProps) => {
  const zorroPay = Decimal.sub(premium, selfPayAmount).toNumber();
  const tooltipText =
    paymentMethod === EmployeePaymentMethodType.COMBINED
      ? tooltipCombinedText(zorroPay ?? 0, selfPayAmount ?? 0)
      : null;
  const {
    label,
    color,
    Icon: MethodIcon,
  } = paymentMethodToProps[paymentMethod];

  return (
    <Tooltip label={tooltipText} position="top" disabled={!tooltipText}>
      <Pill variant="with-icon" color={color} leftSection={<MethodIcon />}>
        {label}
      </Pill>
    </Tooltip>
  );
};

export const initialPremiumStatusToProps: Record<
  InitialPremiumPaymentStatus,
  { label: string; color: PillColor; icon: ReactNode }
> = {
  [InitialPremiumPaymentStatus.COMPLETED_BY_AGENT]: {
    label: 'Completed by agent',
    color: 'green',
    icon: <IconCheck />,
  },
  [InitialPremiumPaymentStatus.COMPLETED_BY_EMPLOYEE]: {
    label: 'Completed by employee',
    color: 'green',
    icon: <IconCheck />,
  },
  [InitialPremiumPaymentStatus.NOT_SET_UP_BY_AGENT]: {
    label: 'Awaiting agent',
    color: 'red',
    icon: <IconX />,
  },
  [InitialPremiumPaymentStatus.NOT_SET_UP_BY_EMPLOYEE]: {
    label: 'Awaiting employee',
    color: 'red',
    icon: <IconX />,
  },
  [InitialPremiumPaymentStatus.SET_UP_BY_AGENT]: {
    label: 'Set up by agent',
    color: 'yellow',
    icon: <IconHalfCircle color="currentColor" />,
  },
  [InitialPremiumPaymentStatus.SET_UP_BY_EMPLOYEE]: {
    label: 'Set up by employee',
    color: 'yellow',
    icon: <IconHalfCircle color="currentColor" />,
  },
};

export const autoPayStatusToProps: Record<
  AutoPayStatus,
  { label: string; color: PillColor; Icon: ComponentType }
> = {
  [AutoPayStatus.NOT_SET_UP_BY_AGENT]: {
    label: 'Awaiting agent',
    color: 'red',
    Icon: IconX,
  },
  [AutoPayStatus.NOT_SET_UP_BY_EMPLOYEE]: {
    label: 'Awaiting employee',
    color: 'red',
    Icon: IconX,
  },
  [AutoPayStatus.SET_UP_BY_AGENT]: {
    label: 'Set up by agent',
    color: 'green',
    Icon: IconCheck,
  },
  [AutoPayStatus.SET_UP_BY_EMPLOYEE]: {
    label: 'Set up by employee',
    color: 'green',
    Icon: IconCheck,
  },
};

interface InitialPaymentStatusBadgeProps {
  status: InitialPremiumPaymentStatus;
}

export const InitialPaymentStatusBadge = ({
  status,
}: InitialPaymentStatusBadgeProps) => {
  const { label, color, icon } = initialPremiumStatusToProps[status];
  return (
    <Pill variant="with-icon" color={color} leftSection={icon}>
      {label}
    </Pill>
  );
};

interface AutoPayStatusBadgeProps {
  status: AutoPayStatus;
}

export const AutoPayStatusBadge = ({ status }: AutoPayStatusBadgeProps) => {
  const { label, color, Icon: StatusIcon } = autoPayStatusToProps[status];
  return (
    <Pill variant="with-icon" color={color} leftSection={<StatusIcon />}>
      {label}
    </Pill>
  );
};
